// src/components/reports/VaccineSaleOrderReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { Checkbox } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import VaccineSaleOrderChart from "./VaccineSaleOrderChart";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineSaleOrderReport = () => {
  const { sessionId } = useAuth();
  
  // ตั้งค่า default date: วันที่ 1 ของเดือนปัจจุบัน และวันที่ปัจจุบัน
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const [filterData, setFilterData] = useState({
    StartDate: firstDayOfMonth,
    EndDate: today,
    WarehouseID: "",
    LivestockType: "",
    VaccineNameTH: "",
  });
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [saleData, setSaleData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);

  // ดึงข้อมูล Warehouse List
  const fetchWarehouseOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouseOptions(response.VaccineWarehousesList || []);
      } else {
        console.error("Failed to fetch warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // ดึงข้อมูล Livestock Type List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const types = response.LivestockTypeList || [];
        setLivestockTypeOptions(types);
        if (types.length === 0) {
          console.warn("LivestockTypeList is empty");
        }
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // เรียก API getVaccineSaleOrderSummary
  const fetchSaleReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrderSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          WarehouseID: filterData.WarehouseID || "",
          LivestockType: filterData.LivestockType || "",
          VaccineNameTH: filterData.VaccineNameTH || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const summary = response.summary || [];
        // เพิ่ม details array สำหรับแต่ละ row
        const summaryWithDetails = summary.map((item) => ({
          ...item,
          details: [],
        }));
        setSaleData(summaryWithDetails);
      } else {
        setSaleData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching sale report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  // ดึงรายละเอียดการขายเมื่อ expand row
  const fetchSaleDetails = async (row) => {
    // ตรวจสอบว่ามี details อยู่แล้วหรือไม่
    if (row.details && row.details.length > 0) {
      return;
    }

    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrderDetails",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          WarehouseID: row.WarehouseID || "",
          LivestockType: row.LivestockType || "",
          VaccineNameTH: row.VaccineNameTH || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const details = response.details || [];
        // อัปเดต details ใน row ที่ถูก expand
        setSaleData((prevData) =>
          prevData.map((item) => {
            if (
              item.WarehouseID === row.WarehouseID &&
              item.LivestockType === row.LivestockType &&
              item.VaccineNameTH === row.VaccineNameTH
            ) {
              return { ...item, details: details };
            }
            return item;
          })
        );
      }
    } catch (error) {
      console.error("Error fetching sale details:", error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouseOptions();
      fetchLivestockTypeOptions();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อคลัง</span>
        </HStack>
      ),
      selector: (row) => row.WarehouseName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนจำหน่าย (ขวด)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantitySale)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนจำหน่าย (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantitySaleDoses)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ราคา/ขวด</span>
        </HStack>
      ),
      selector: (row) => {
        const quantity = Number(row.TotalQuantitySale) || 0;
        const value = Number(row.TotalValueSale) || 0;
        const unitPrice = quantity > 0 ? (value / quantity).toFixed(2) : 0;
        return Number(unitPrice)?.toLocaleString();
      },
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>มูลค่าจำหน่าย</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalValueSale)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
  ];

  const detailColumns = [
    {
      name: "เลขที่ใบขาย",
      selector: (row) => row.TransactionID || "",
      sortable: true,
    },
    {
      name: "วันที่ขาย",
      selector: (row) => row.RequestDate ? new Date(row.RequestDate).toLocaleDateString("th-TH") : "",
      sortable: true,
    },
    {
      name: "ชื่อเกษตรกร",
      selector: (row) => row.FarmerName || "",
      sortable: true,
    },
    {
      name: "เบอร์โทร",
      selector: (row) => row.FarmerTel || "",
      sortable: true,
    },
    {
      name: "จำนวนจำหน่าย (ขวด)",
      selector: (row) => Number(row.RequestQuantity)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "จำนวนจำหน่าย (โด๊ส)",
      selector: (row) => Number(row.RequestQuantityDoses)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "มูลค่าจำหน่าย",
      selector: (row) => Number(row.TotalPrice)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "Lot ID",
      selector: (row) => row.LotID || "",
      sortable: true,
    },
  ];

  const ExpandedComponent = ({ data }) => {
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // ดึงรายละเอียดเมื่อ expand
    React.useEffect(() => {
      if (data.details && data.details.length === 0) {
        setIsLoadingDetails(true);
        fetchSaleDetails(data).finally(() => {
          setIsLoadingDetails(false);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.WarehouseID, data.LivestockType, data.VaccineNameTH]);

    return (
      <Box p={4} bg="gray.50">
        {isLoadingDetails ? (
          <Box textAlign="center" p={4}>
            กำลังโหลดข้อมูล...
          </Box>
        ) : data.details && data.details.length > 0 ? (
          <DataTable
            columns={detailColumns}
            data={data.details}
            customStyles={tableCustomStyles}
            dense
            noHeader
          />
        ) : (
          <Box textAlign="center" p={4}>
            ไม่พบข้อมูลรายละเอียด
          </Box>
        )}
      </Box>
    );
  };

  // ฟังก์ชันสำหรับดึงรายละเอียดทั้งหมด
  const fetchAllDetails = async () => {
    const allDetails = [];
    for (const item of saleData) {
      if (!item.details || item.details.length === 0) {
        try {
          const response = await apiService({
            method: "POST",
            url: "/getVaccineSaleOrderDetails",
            data: {
              StartDate: filterData.StartDate || "",
              EndDate: filterData.EndDate || "",
              WarehouseID: item.WarehouseID || "",
              LivestockType: item.LivestockType || "",
              VaccineNameTH: item.VaccineNameTH || "",
            },
            sessionId: sessionId || "",
          });
          if (response.result === "success") {
            allDetails.push(...(response.details || []));
          }
        } catch (error) {
          console.error("Error fetching details:", error);
        }
      } else {
        allDetails.push(...item.details);
      }
    }
    return allDetails;
  };

  const exportToExcel = async (includeDetails = false) => {
    const wb = XLSX.utils.book_new();
    
    // สร้างข้อมูลสรุป
    const summaryData = saleData.map((item) => {
      const quantity = Number(item.TotalQuantitySale) || 0;
      const value = Number(item.TotalValueSale) || 0;
      const unitPrice = quantity > 0 ? (value / quantity).toFixed(2) : 0;
      return {
        ชื่อคลัง: item.WarehouseName,
        "ชื่อวัคซีน (TH)": item.VaccineNameTH,
        "จำนวนจำหน่าย (ขวด)": item.TotalQuantitySale,
        "จำนวนจำหน่าย (โด๊ส)": item.TotalQuantitySaleDoses,
        "ราคา/ขวด": unitPrice,
        มูลค่าจำหน่าย: item.TotalValueSale,
      };
    });

    // เพิ่มข้อมูลเงื่อนไข
    const warehouseName = filterData.WarehouseID
      ? warehouseOptions.find((w) => w.WarehouseID === filterData.WarehouseID)?.WarehouseName || ""
      : "";
    const startDate = filterData.StartDate
      ? new Date(filterData.StartDate).toLocaleDateString("th-TH")
      : "";
    const endDate = filterData.EndDate
      ? new Date(filterData.EndDate).toLocaleDateString("th-TH")
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    // สร้าง header
    const headerData = [
      { A: "รายงานการจำหน่ายวัคซีน (งบจำหน่าย)" + (warehouseName ? ` : คลัง ${warehouseName}` : "") },
      { A: `ข้อมูลการจำหน่ายระหว่างวันที่ ${startDate} ถึงวันที่ ${endDate}` },
      { A: `วันที่รายงาน : ${reportDate}` },
      {},
    ];

    const ws = XLSX.utils.json_to_sheet(headerData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, summaryData, { origin: 5 });

    // จัด alignment ตัวเลข (คอลัมน์ C-F คือ 2-5)
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let row = 5; row <= range.e.r; row++) {
      // คอลัมน์ C (2): จำนวนจำหน่าย (ขวด)
      // คอลัมน์ D (3): จำนวนจำหน่าย (โด๊ส)
      // คอลัมน์ E (4): ราคา/ขวด
      // คอลัมน์ F (5): มูลค่าจำหน่าย
      for (let col = 2; col <= 5; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].z = "#,##0.00";
        // ตัวเลขให้ align right
        if (!ws[cellAddress].s) ws[cellAddress].s = {};
        ws[cellAddress].s.alignment = { horizontal: "right" };
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "สรุปการจำหน่าย");

    // ถ้าต้องการรายละเอียด
    if (includeDetails) {
      const allDetails = await fetchAllDetails();
      const detailData = allDetails.map((item) => ({
        เลขที่ใบขาย: item.TransactionID,
        วันที่ขาย: item.RequestDate ? new Date(item.RequestDate).toLocaleDateString("th-TH") : "",
        ชื่อเกษตรกร: item.FarmerName,
        เบอร์โทร: item.FarmerTel,
        "จำนวนจำหน่าย (ขวด)": item.RequestQuantity,
        "จำนวนจำหน่าย (โด๊ส)": item.RequestQuantityDoses,
        มูลค่าจำหน่าย: item.TotalPrice,
        "Lot ID": item.LotID,
      }));

      const wsDetails = XLSX.utils.json_to_sheet(detailData);
      // จัด alignment ตัวเลข (คอลัมน์ E-G คือ 4-6)
      const detailRange = XLSX.utils.decode_range(wsDetails["!ref"] || "A1");
      for (let row = 1; row <= detailRange.e.r; row++) {
        // คอลัมน์ E (4): จำนวนจำหน่าย (ขวด)
        // คอลัมน์ F (5): จำนวนจำหน่าย (โด๊ส)
        // คอลัมน์ G (6): มูลค่าจำหน่าย
        for (let col = 4; col <= 6; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!wsDetails[cellAddress]) continue;
          wsDetails[cellAddress].z = "#,##0.00";
          if (!wsDetails[cellAddress].s) wsDetails[cellAddress].s = {};
          wsDetails[cellAddress].s.alignment = { horizontal: "right" };
        }
      }
      XLSX.utils.book_append_sheet(wb, wsDetails, "รายละเอียดการจำหน่าย");
    }

    XLSX.writeFile(wb, "VaccineSaleOrderReport.xlsx");
  };

  const exportToPDF = async (includeDetails = false) => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    // ข้อมูลเงื่อนไข
    const warehouseName = filterData.WarehouseID
      ? warehouseOptions.find((w) => w.WarehouseID === filterData.WarehouseID)?.WarehouseName || ""
      : "";
    const startDate = filterData.StartDate
      ? new Date(filterData.StartDate).toLocaleDateString("th-TH")
      : "";
    const endDate = filterData.EndDate
      ? new Date(filterData.EndDate).toLocaleDateString("th-TH")
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    doc.setFontSize(16);
    doc.text("รายงานการจำหน่ายวัคซีน (งบจำหน่าย)" + (warehouseName ? ` : คลัง ${warehouseName}` : ""), 14, 10);
    doc.setFontSize(10);
    doc.text(`ข้อมูลการจำหน่ายระหว่างวันที่ ${startDate} ถึงวันที่ ${endDate}`, 14, 18);
    doc.text(`วันที่รายงาน : ${reportDate}`, 14, 24);

    autoTable(doc, {
      startY: 30,
      head: [
        [
          "ชื่อคลัง",
          "ชื่อวัคซีน (TH)",
          "จำนวนจำหน่าย (ขวด)",
          "จำนวนจำหน่าย (โด๊ส)",
          "ราคา/ขวด",
          "มูลค่าจำหน่าย",
        ],
      ],
      body: saleData.map((item) => {
        const quantity = Number(item.TotalQuantitySale) || 0;
        const value = Number(item.TotalValueSale) || 0;
        const unitPrice = quantity > 0 ? (value / quantity).toFixed(2) : 0;
        return [
          item.WarehouseName,
          item.VaccineNameTH,
          Number(item.TotalQuantitySale)?.toLocaleString(),
          Number(item.TotalQuantitySaleDoses)?.toLocaleString(),
          Number(unitPrice)?.toLocaleString(),
          Number(item.TotalValueSale)?.toLocaleString(),
        ];
      }),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },      
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fontSize: 11,
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 60, halign: "left" },
        1: { cellWidth: 90, halign: "left" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 30, halign: "right" },
        4: { cellWidth: 30, halign: "right" },
        5: { cellWidth: 30, halign: "right" },
      },
      didParseCell: (data) => {
        // จัด alignment ตัวเลข
        if (data.column.index >= 2 && data.column.index <= 5) {
          data.cell.styles.halign = "right";
        }
      },
    });

    // ถ้าต้องการรายละเอียด
    if (includeDetails) {
      const allDetails = await fetchAllDetails();
      if (allDetails.length > 0) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text("รายละเอียดการจำหน่าย", 14, 10);
        
        autoTable(doc, {
          startY: 20,
          head: [
            [
              "เลขที่ใบขาย",
              "วันที่ขาย",
              "ชื่อเกษตรกร",
              "เบอร์โทร",
              "จำนวนจำหน่าย (ขวด)",
              "จำนวนจำหน่าย (โด๊ส)",
              "มูลค่าจำหน่าย",
              "Lot ID",
            ],
          ],
          body: allDetails.map((item) => [
            item.TransactionID,
            item.RequestDate ? new Date(item.RequestDate).toLocaleDateString("th-TH") : "",
            item.FarmerName || "",
            item.FarmerTel || "",
            Number(item.RequestQuantity)?.toLocaleString(),
            Number(item.RequestQuantityDoses)?.toLocaleString(),
            Number(item.TotalPrice)?.toLocaleString(),
            item.LotID || "",
          ]),
          styles: {
            font: fontBase64 ? "Sarabun" : "helvetica",
            fontSize: 10,
            cellPadding: 1,
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255],
          },
          headStyles: {
            font: fontBase64 ? "Sarabun" : "helvetica",
            fontStyle: "normal",
            fontSize: 10,
            halign: "center",
            fillColor: [211, 211, 211],
            textColor: "black",
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
          },
          columnStyles: {
            0: { cellWidth: 40, halign: "left" },
            1: { cellWidth: 30, halign: "left" },
            2: { cellWidth: 40, halign: "left" },
            3: { cellWidth: 30, halign: "left" },
            4: { cellWidth: 35, halign: "right" },
            5: { cellWidth: 35, halign: "right" },
            6: { cellWidth: 35, halign: "right" },
            7: { cellWidth: 30, halign: "left" },
          },
        });
      }
    }

    doc.save("VaccineSaleOrderReport.pdf");
  };

  const printTable = async (includeDetails = false) => {
    const warehouseName = filterData.WarehouseID
      ? warehouseOptions.find((w) => w.WarehouseID === filterData.WarehouseID)?.WarehouseName || ""
      : "";
    const startDate = filterData.StartDate
      ? new Date(filterData.StartDate).toLocaleDateString("th-TH")
      : "";
    const endDate = filterData.EndDate
      ? new Date(filterData.EndDate).toLocaleDateString("th-TH")
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    let detailsHTML = "";
    if (includeDetails) {
      const allDetails = await fetchAllDetails();
      if (allDetails.length > 0) {
        detailsHTML = `
          <h2 style="margin-top: 30px;">รายละเอียดการจำหน่าย</h2>
          <table>
            <thead>
              <tr>
                <th>เลขที่ใบขาย</th>
                <th>วันที่ขาย</th>
                <th>ชื่อเกษตรกร</th>
                <th>เบอร์โทร</th>
                <th class="text-right">จำนวนจำหน่าย (ขวด)</th>
                <th class="text-right">จำนวนจำหน่าย (โด๊ส)</th>
                <th class="text-right">มูลค่าจำหน่าย</th>
                <th>Lot ID</th>
              </tr>
            </thead>
            <tbody>
              ${allDetails
                .map(
                  (item) => `
                <tr>
                  <td>${item.TransactionID || ""}</td>
                  <td>${item.RequestDate ? new Date(item.RequestDate).toLocaleDateString("th-TH") : ""}</td>
                  <td>${item.FarmerName || ""}</td>
                  <td>${item.FarmerTel || ""}</td>
                  <td class="text-right">${Number(item.RequestQuantity)?.toLocaleString()}</td>
                  <td class="text-right">${Number(item.RequestQuantityDoses)?.toLocaleString()}</td>
                  <td class="text-right">${Number(item.TotalPrice)?.toLocaleString()}</td>
                  <td>${item.LotID || ""}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        `;
      }
    }

    const printContent = `
      <html>
        <head>
          <title>รายงานการจำหน่ายวัคซีน (งบจำหน่าย)</title>
          <style>
            body { font-family: 'THSarabunNew', Arial, sans-serif; margin: 20px; }
            h2 { text-align: center; margin-bottom: 10px; }
            .info-section { margin-bottom: 15px; }
            .info-line { margin: 5px 0; font-size: 14px; }
            table { border-collapse: collapse; width: 100%; margin-top: 10px; }
            th, td { border: 1px solid black; padding: 8px; }
            th { background-color: #d3d3d3; text-align: center; font-weight: bold; }
            td { text-align: left; }
            .text-right { text-align: right !important; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h2>รายงานการจำหน่ายวัคซีน (งบจำหน่าย)${warehouseName ? ` : คลัง ${warehouseName}` : ""}</h2>
          <div class="info-section">
            <div class="info-line">ข้อมูลการจำหน่ายระหว่างวันที่ ${startDate} ถึงวันที่ ${endDate}</div>
            <div class="info-line">วันที่รายงาน : ${reportDate}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>ชื่อคลัง</th>
                <th>ชื่อวัคซีน (TH)</th>
                <th>จำนวนจำหน่าย (ขวด)</th>
                <th>จำนวนจำหน่าย (โด๊ส)</th>
                <th>ราคา/ขวด</th>
                <th>มูลค่าจำหน่าย</th>
              </tr>
            </thead>
            <tbody>
              ${saleData
                .map(
                  (item) => {
                    const quantity = Number(item.TotalQuantitySale) || 0;
                    const value = Number(item.TotalValueSale) || 0;
                    const unitPrice = quantity > 0 ? (value / quantity).toFixed(2) : 0;
                    return `
                    <tr>
                      <td>${item.WarehouseName}</td>
                      <td>${item.VaccineNameTH}</td>
                      <td class="text-right">${Number(item.TotalQuantitySale)?.toLocaleString()}</td>
                      <td class="text-right">${Number(item.TotalQuantitySaleDoses)?.toLocaleString()}</td>
                      <td class="text-right">${Number(unitPrice)?.toLocaleString()}</td>
                      <td class="text-right">${Number(item.TotalValueSale)?.toLocaleString()}</td>
                    </tr>
                  `;
                  }
                )
                .join("")}
            </tbody>
          </table>
          ${detailsHTML}
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการจำหน่ายวัคซีน (งบจำหน่าย)" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการจำหน่ายวัคซีน (งบจำหน่าย)"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                คลัง
              </FormLabel>
              <Select
                name="WarehouseID"
                value={filterData.WarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลัง (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {warehouseOptions.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Select
                name="LivestockType"
                value={filterData.LivestockType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {livestockTypeOptions.map((type) => (
                  <option
                    key={type.LivestockTypeID}
                    value={type.LivestockTypeID}
                  >
                    {type.LivestockTypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineNameTH"
                value={filterData.VaccineNameTH}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineNameTH}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() => {
                  const today = new Date();
                  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  setFilterData({
                    StartDate: firstDayOfMonth,
                    EndDate: today,
                    WarehouseID: "",
                    LivestockType: "",
                    VaccineNameTH: "",
                  });
                }}
              />
              <Search onClick={fetchSaleReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {saleData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการการจำหน่ายวัคซีน (งบจำหน่าย)"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Checkbox
              isChecked={includeDetails}
              onChange={(e) => setIncludeDetails(e.target.checked)}
              colorScheme="iBlue"
            >
              รวมรายละเอียดการจำหน่าย
            </Checkbox>
            <Spacer />
            <ExportExcel onClick={async () => await exportToExcel(includeDetails)} />
            <ExportPdf onClick={async () => await exportToPDF(includeDetails)} />
            <Print onClick={async () => await printTable(includeDetails)} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={saleData}
              pagination
              highlightOnHover
              striped
              responsive
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              onRowExpandToggled={(toggled, row) => {
                if (toggled && row.details.length === 0) {
                  fetchSaleDetails(row);
                }
              }}
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {saleData.length > 0 && (
        <VaccineSaleOrderChart
          data={saleData}
          warehouseOptions={warehouseOptions}
        />
      )}
    </Box>
  );
};

export default VaccineSaleOrderReport;
